/*
 * Offline edits, reserved invoice numbers and conflicts, with no browser APIs, so server/test-offline7.js runs it in Node
 * (like outbox-core.ts). Wave 7, docs/implemented-offline7.md.
 *
 *   baseOf(record, body)        the values the device started from, for the fields an edit sends
 *   editFields(item)            for a conflicted edit, each field: what it was, what you typed, what the server has now
 *   resolveEdit(item, ...)      the person's choice as a new outbox item (a new key: its body differs), or null when nothing
 *                               of theirs is left to send
 *   freeNumber(blocks, items)   the next reserved invoice number this device may use, or null when its block is used up
 *   shownTotal(body)            what a new invoice's total is on the device, with no server to work it out
 *
 * A record's fields are compared as values, not as text: "1180" and "1180.00" are the same, null and "" are the same, and
 * an invoice's lines are compared by item, name, quantity, rate, discount and typed tax rate.
 */

import type { OutboxItem } from "./outbox-core";

type Rec = Record<string, unknown>;

/** A value as it compares: blank is null, numbers (or numeric text) as numbers, objects key by key. */
export function norm(v: unknown): unknown {
  if (v === undefined || v === null || v === "") return null;
  if (typeof v === "number") return Math.round(v * 1e6) / 1e6;
  if (typeof v === "string") {
    const s = v.trim();
    if (s === "") return null;
    return /^-?\d+(\.\d+)?$/.test(s) ? Math.round(Number(s) * 1e6) / 1e6 : s;
  }
  if (Array.isArray(v)) return v.map(norm);
  if (typeof v === "object") {
    const o = v as Rec;
    const keys = Object.keys(o).filter((k) => norm(o[k]) !== null).sort();
    return keys.length ? Object.fromEntries(keys.map((k) => [k, norm(o[k])])) : null;
  }
  return v;
}

export const same = (a: unknown, b: unknown) => JSON.stringify(norm(a)) === JSON.stringify(norm(b));

/** "1:30", "1.5" or 90 (minutes) as minutes. */
function minutesOf(v: unknown): number | null {
  if (typeof v === "number") return v;
  if (typeof v !== "string") return null;
  const m = /^(\d{1,3})(?::(\d{2})|\.(\d{1,2}))?$/.exec(v.trim());
  if (!m) return null;
  return Number(m[1]) * 60 + (m[2] ? Number(m[2]) : m[3] ? Math.round(Number(`0.${m[3]}`) * 60) : 0);
}

/** An invoice or estimate line, as a form sends it or as the server shows it, in the shape both compare in. */
function lineOf(l: Rec) {
  return {
    item_id: l.item_id ?? null,
    name: l.name ?? null,
    quantity: l.quantity ?? null,
    rate: l.rate ?? null,
    discount_type: l.discount ? (l.discount_type ?? null) : null,
    discount: l.discount ?? null,
    tax_rate: "tax_rate_in" in l ? l.tax_rate_in : (l.tax_rate ?? null),
    description: l.description ?? null,
  };
}

/** The value of one field of an edit's body, as the record (the server's shape) holds it. */
export function fieldOf(record: unknown, key: string): unknown {
  const r = (record ?? {}) as Rec;
  if (key === "lines" && Array.isArray(r.lines)) return (r.lines as Rec[]).map(lineOf);
  if (key === "hours" && typeof r.minutes === "number") return r.minutes;
  return r[key];
}

/** The same field of the body, comparable with fieldOf. */
function bodyValue(key: string, v: unknown): unknown {
  if (key === "lines" && Array.isArray(v)) return (v as Rec[]).map(lineOf);
  if (key === "hours") return minutesOf(v);
  return v;
}

/** What the device started from, for each field the edit sends. */
export function baseOf(record: unknown, body: Rec): Rec {
  return Object.fromEntries(Object.keys(body).map((k) => [k, fieldOf(record, k) ?? null]));
}

export type FieldState = {
  key: string;
  base: unknown;
  mine: unknown;
  theirs: unknown;
  /** Changed on this device. */
  mineChanged: boolean;
  /** Changed on the server since. */
  theirsChanged: boolean;
  /** Both changed, to different values: the person chooses. */
  clash: boolean;
};

/** Each field of a conflicted edit. Fields that are the same everywhere are left out. */
export function editFields(item: OutboxItem): FieldState[] {
  const body = (item.body ?? {}) as Rec;
  const base = item.base ?? {};
  const current = item.conflict?.current;
  return Object.keys(body)
    .map((key) => {
      const mine = bodyValue(key, body[key]);
      const b = base[key] ?? null;
      const theirs = fieldOf(current, key) ?? null;
      const mineChanged = !same(mine, b);
      const theirsChanged = !same(theirs, b);
      return { key, base: b, mine, theirs, mineChanged, theirsChanged, clash: mineChanged && theirsChanged && !same(mine, theirs) };
    })
    .filter((f) => f.mineChanged || f.theirsChanged);
}

export type Choice = "mine" | "theirs";

/**
 * The person's choice as a new item. "mine": every field changed on this device is sent; "theirs": nothing is (null back);
 * a map: field by field. A field only the server changed is always kept from the server; a field only this device changed
 * is sent unless the map says "theirs". The new item checks against the version the server has now, so a further change
 * meanwhile is caught again.
 */
export function resolveEdit(item: OutboxItem, choice: Choice | Record<string, Choice>, newId: string): OutboxItem | null {
  if (choice === "theirs" || !item.conflict) return null;
  const body = (item.body ?? {}) as Rec;
  const keep: Rec = {};
  for (const f of editFields(item)) {
    if (!f.mineChanged) continue;
    const pick = typeof choice === "string" ? "mine" : (choice[f.key] ?? "mine");
    if (pick === "mine") keep[f.key] = body[f.key];
  }
  if (!Object.keys(keep).length) return null;
  // Wave 9: a PUT replaces a whole setting, so every field goes: mine where kept, the server's where not.
  const whole = item.method === "PUT" ? Object.fromEntries(Object.keys(body).map((k) => [k, k in keep ? keep[k] : (fieldOf(item.conflict?.current, k) ?? body[k])])) : keep;
  return {
    ...item,
    id: newId,
    body: whole,
    base: baseOf(item.conflict.current, whole),
    version: item.conflict.version,
    conflict: null,
    state: "pending",
    error: null,
    attempts: 0,
    created_at: item.created_at,
  };
}

/* ---------- reserved invoice numbers ---------- */

export type KeptBlock = { id: string; period: string; free: string[]; expires_at?: string | null };

/** Wave 8: the blocks whose time is not over (a block with no end, from before wave 8, counts as live). Wave 9: `now` is the server's time (serverNow). */
export const liveBlocks = (blocks: KeptBlock[], now = Date.now()) => blocks.filter((b) => !b.expires_at || Date.parse(b.expires_at) > now);

/**
 * Wave 9: the server's clock minus this device's, from the server_time of an answer to a request sent at `sent` and answered
 * at `got` (both this device's clock): the server read its clock about half way. Null without a server time.
 */
export function skewOf(serverTime: string | null | undefined, sent: number, got: number): number | null {
  const at = serverTime ? Date.parse(serverTime) : NaN;
  return Number.isFinite(at) ? Math.round(at - (sent + got) / 2) : null;
}
/** Wave 9: the server's time, by this device's clock corrected with the kept skew. */
export const serverNow = (skew: number | null | undefined, device = Date.now()) => device + (skew ?? 0);

/**
 * The numbers queued issues already hold (pending or conflicted; flagged ones were used) in this organization, and (wave 8)
 * those a refuse item is still to mark on the server.
 */
export const numbersHeld = (items: OutboxItem[], org: number) =>
  new Set(
    items
      .filter((i) => (i.org ?? 1) === org && ((i.kind === "issue" && i.state !== "flagged") || i.kind === "refuse"))
      .flatMap((i) => (i.kind === "refuse" ? ((i.body as { numbers?: unknown })?.numbers as unknown[]) ?? [] : [(i.body as { number?: unknown })?.number]))
      .filter((n): n is string => typeof n === "string"),
  );

/** The next number this device may issue with, or null when every reserved number is used or waiting to be sent. */
export function freeNumber(blocks: KeptBlock[], held: Set<string>, period?: string): { number: string; block: string } | null {
  for (const b of blocks) {
    if (period && b.period !== period) continue;
    const n = b.free.find((x) => !held.has(x));
    if (n) return { number: n, block: b.id };
  }
  return null;
}

/** Indian financial year of a calendar day: 2026-10-30 is 2026-27 (server/numbering.js fyOf). */
export function fyOfDay(day: string) {
  const y = Number(day.slice(0, 4));
  const start = Number(day.slice(5, 7)) >= 4 ? y : y - 1;
  return `${start}-${String((start + 1) % 100).padStart(2, "0")}`;
}

/**
 * A new invoice's total on the device: every rate includes GST, as on the form, so the total is the lines' amounts less
 * discounts, plus shipping and adjustment. The server works the GST split (and any price rule) out again at replay, and
 * flags the invoice when its total differs. `places`: the decimals of the customer's currency (2 for INR, 0 for JPY, 3 for
 * KWD), so the figure is in the currency the server compares it in.
 */
export function shownTotal(body: { lines?: Rec[]; discount_type?: unknown; discount?: unknown; shipping?: unknown; adjustment?: unknown }, places = 2) {
  const unit = 10 ** places;
  const minor = (v: unknown) => {
    const n = Number(typeof v === "string" ? v.replace(/,/g, "") : v);
    return Number.isFinite(n) ? Math.round(n * unit) : 0;
  };
  let sum = 0;
  for (const l of body.lines ?? []) {
    const gross = Math.round(Number(l.quantity ?? 0) * minor(l.rate));
    const off = l.discount_type === "percent" ? Math.round((gross * Number(l.discount ?? 0)) / 100) : minor(l.discount);
    sum += gross - off;
  }
  const docOff = body.discount_type === "percent" ? Math.round((sum * Number(body.discount ?? 0)) / 100) : minor(body.discount);
  return ((sum - docOff + minor(body.shipping) + minor(body.adjustment)) / unit).toFixed(places);
}

/** Decimals of a currency (KWD 3, JPY 0), from the browser's own currency data; 2 when it doesn't know the code. */
export function currencyPlaces(code: string | null | undefined) {
  if (!code) return 2;
  try {
    return new Intl.NumberFormat("en", { style: "currency", currency: code }).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    return 2;
  }
}

/* ---------- what the server is told about this device (never shown on a screen) ---------- */

/** "Android · Chrome": how the numbering report names a device. */
export function deviceLabel(ua: string) {
  const os = /Android/.test(ua) ? "Android" : /iPhone|iPad/.test(ua) ? "iOS" : /Windows/.test(ua) ? "Windows" : /Mac OS/.test(ua) ? "Mac" : /Linux/.test(ua) ? "Linux" : "Device";
  const browser = /Edg\//.test(ua) ? "Edge" : /SamsungBrowser/.test(ua) ? "Samsung Internet" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : "browser";
  return `${os} · ${browser}`;
}

/** The audit note on numbers given back at sign-out. */
export const RELEASE_NOTE = "Signed out on this device.";
/** Wave 8: the audit note on numbers that were on invoices still unsent at sign-out (never released: used on device, refused). */
/** Wave 8: the audit note on a number whose unsent offline issue was discarded on the device (used on device, refused). */
export const DISCARDED_NOTE = "Printed on an invoice issued offline on this device, which was discarded there before it reached the server.";
export const SIGNED_OUT_NOTE = "On an invoice issued offline on this device that was still unsent when the person signed out.";
